import React, { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  CloseButton,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconDownload, IconEye, IconFileTypePdf, IconTrash, IconUpload } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { ApiService } from '../../api/api-service';
import { EnrollmentAttachment } from '../types/package.types';

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,application/pdf';

export const uploadEnrollmentAttachments = async (packageId: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  await ApiService.getInstance().post(`/package/${packageId}/enrollment-attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

interface EnrollmentAttachmentsProps {
  /** Empty while creating a package — files are queued and uploaded after save. */
  packageId: string;
  pendingFiles: File[];
  onPendingFilesChange: (files: File[]) => void;
}

export default function EnrollmentAttachments({
  packageId,
  pendingFiles,
  onPendingFilesChange,
}: EnrollmentAttachmentsProps) {
  const [attachments, setAttachments] = useState<EnrollmentAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const fetchAttachments = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    try {
      const res = await ApiService.getInstance().get(`/package/${packageId}/enrollment-attachments`);
      setAttachments(res?.data?.attachments ?? []);
    } catch (err) {
      console.error('Failed to fetch enrollment screenshots:', err);
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    setAttachments([]);
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFiles = async (files: File[]) => {
    if (!files.length) return;

    if (!packageId) {
      onPendingFilesChange([...pendingFiles, ...files]);
      return;
    }

    setUploading(true);
    try {
      await uploadEnrollmentAttachments(packageId, files);
      showNotification({
        title: 'Success',
        message: `${files.length} file(s) uploaded`,
        color: 'green',
      });
      await fetchAttachments();
    } catch (err: any) {
      showNotification({
        title: 'Upload failed',
        message: err?.response?.data?.error || err?.message || 'Failed to upload enrollment screenshot',
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachment: EnrollmentAttachment) => {
    setDeletingKey(attachment.key);
    try {
      await ApiService.getInstance().delete(`/package/${packageId}/enrollment-attachments`, {
        params: { key: attachment.key },
      });
      setAttachments((prev) => prev.filter((a) => a.key !== attachment.key));
      showNotification({
        title: 'Deleted',
        message: `${attachment.fileName} deleted`,
        color: 'green',
      });
    } catch (err: any) {
      showNotification({
        title: 'Delete failed',
        message: err?.response?.data?.error || err?.message || 'Failed to delete enrollment screenshot',
        color: 'red',
      });
      await fetchAttachments();
    } finally {
      setDeletingKey(null);
      setConfirmDeleteKey(null);
    }
  };

  const openViewer = async () => {
    setConfirmDeleteKey(null);
    setViewOpen(true);
    // Presigned URLs expire, so refresh them each time the viewer opens
    await fetchAttachments();
  };

  return (
    <Box>
      <Text fw={500} size="sm" mb={6}>
        Upload Enrollment Screenshot
      </Text>

      <Group gap="sm">
        <FileButton onChange={handleFiles} accept={ACCEPTED_TYPES} multiple>
          {(props) => (
            <Button
              {...props}
              variant="light"
              leftSection={<IconUpload size={16} />}
              loading={uploading}
            >
              Upload
            </Button>
          )}
        </FileButton>

        <Tooltip label="Available after the package is saved" disabled={Boolean(packageId)}>
          <Button
            variant="outline"
            leftSection={<IconEye size={16} />}
            onClick={openViewer}
            disabled={!packageId}
            rightSection={
              packageId ? (
                loading ? <Loader size="xs" /> : <Badge size="sm" variant="light">{attachments.length}</Badge>
              ) : undefined
            }
          >
            View
          </Button>
        </Tooltip>
      </Group>

      {!packageId && pendingFiles.length > 0 && (
        <Stack gap={4} mt="xs">
          <Text size="xs" c="dimmed">
            These files will be uploaded when the package is created:
          </Text>
          {pendingFiles.map((file, idx) => (
            <Group key={`${file.name}-${idx}`} gap={4}>
              <Text size="sm">{file.name}</Text>
              <CloseButton
                size="sm"
                aria-label={`Remove ${file.name}`}
                onClick={() => onPendingFilesChange(pendingFiles.filter((_, i) => i !== idx))}
              />
            </Group>
          ))}
        </Stack>
      )}

      <Modal
        opened={viewOpen}
        onClose={() => setViewOpen(false)}
        title="Enrollment Screenshots"
        size="xl"
        centered
        zIndex={300}
      >
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : attachments.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No enrollment screenshots uploaded yet.
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {attachments.map((a) => (
              <Stack key={a.key} gap={4}>
                <Anchor href={a.url} target="_blank" rel="noopener noreferrer">
                  {a.contentType === 'application/pdf' ? (
                    <Group
                      justify="center"
                      h={200}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}
                    >
                      <IconFileTypePdf size={48} />
                    </Group>
                  ) : (
                    <Image
                      src={a.url}
                      alt={a.fileName}
                      h={200}
                      fit="contain"
                      radius="md"
                      style={{ border: '1px solid #e5e7eb' }}
                    />
                  )}
                </Anchor>
                <Text size="sm" truncate title={a.fileName}>
                  {a.fileName}
                </Text>
                <Text size="xs" c="dimmed">
                  {new Date(a.uploadedAt).toLocaleString('en-GB')}
                </Text>
                {confirmDeleteKey === a.key ? (
                  <Group gap="xs">
                    <Text size="sm" c="red">
                      Delete this file?
                    </Text>
                    <Button
                      size="xs"
                      color="red"
                      loading={deletingKey === a.key}
                      onClick={() => handleDelete(a)}
                    >
                      Yes, delete
                    </Button>
                    <Button
                      size="xs"
                      variant="default"
                      disabled={deletingKey === a.key}
                      onClick={() => setConfirmDeleteKey(null)}
                    >
                      Cancel
                    </Button>
                  </Group>
                ) : (
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      component="a"
                      href={a.downloadUrl}
                    >
                      Download
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => setConfirmDeleteKey(a.key)}
                    >
                      Delete
                    </Button>
                  </Group>
                )}
              </Stack>
            ))}
          </SimpleGrid>
        )}
      </Modal>
    </Box>
  );
}
